"use client";

import Link from "next/link";
import Image from "next/image";
import { Database, Linkedin } from "lucide-react";
import { WebsiteCtaLink } from "@/app/(website)/_components/website-cta-link";

export default function CompanyPage() {
  return (
    <div className="relative">
      {/* Hero Section - Start with WHY */}
      <section className="pt-20 pb-16 md:pt-32 md:pb-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
              We believe commerce decisions should be driven by understanding, not guesswork.
            </h1>
            <p className="text-xl md:text-2xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
              Storita helps merchants understand what's happening in their business - and why - through conversation.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story - Moved up as the strongest section */}
      <section className="py-24 bg-slate-900/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Story</h2>
            <div className="space-y-6 text-slate-400 text-lg leading-relaxed">
              <p>
                For years, we worked hands-on with eCommerce businesses. Not just designing sites, but being pulled into the real stuff - launches that didn't go smoothly, tracking that broke, campaigns that scaled and then suddenly didn't. We helped stores grow from zero to real monthly revenue, and we stayed around when things got complicated.
              </p>
              <p>
                At the same time, we were running our own eCommerce store. Dealing with inventory, suppliers, customers, cashflow - all the things that don't show up neatly in a dashboard. And more than once, we found ourselves asking the same question our clients were asking: what is actually going on right now?
              </p>
              <p>
                What kept surprising us wasn't the lack of data. There was always more data.<br />
                Dashboards, reports, exports - plenty of numbers.<br />
                What was missing was the connection between them, and the time it took to notice when something was drifting in the wrong direction.
              </p>
              <p>
                Margins didn't disappear overnight. Inventory didn't become a problem in one week. Returns crept up quietly. The signs were there - we just didn't always catch them early, especially when we were busy doing everything else.
              </p>
              <p>
                Storita came out of that frustration.<br />
                Not from a big plan to “build a platform,” but from a desire for a better way to track what actually drives conversions - without staring at dashboards all day or needing a team to watch the numbers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pull Quote - Memorable conviction */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="text-2xl md:text-3xl font-medium text-slate-200 leading-relaxed">
              "Data doesn't drive decisions.<br />
              <span className="text-purple-400">Understanding does.</span>"
            </blockquote>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="py-24 bg-slate-900/30">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-8">Why We Exist</h2>
          <p className="text-xl text-slate-400 leading-relaxed">
            To make deep commercial insight accessible through conversation - so every merchant can operate with clarity once reserved for the largest brands.
          </p>
        </div>
      </section>

      {/* What We Built - Brief, focused */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">What We Built</h2>
            <div className="text-xl text-slate-400 leading-relaxed space-y-6 text-center">
              <p>
                Storita connects core store data - orders, products, customers, payments, marketing inputs, and operations - into a single reasoning layer.
              </p>
              <p>
                Instead of charts and reports, it enables an ongoing conversation with the business itself. Merchants ask questions in plain language and get clear, actionable answers - privately, on their own terms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-slate-900/30">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-3xl font-bold mb-4 text-center">Our Team</h2>
          <p className="text-xl text-slate-400 text-center mb-16">Meet our Founders</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Doron Herzlich",
                role: "Co-founder & CEO",
                image: "/website/images/team/doron.png",
                bio: "Hi-tech entrepreneur & CEO with 25+ years building and leading companies, including three M&As and two IPOs.",
                linkedin: "https://www.linkedin.com/in/doronherzlich/"
              },
              {
                name: "Yori Nelken",
                role: "Co-founder & Board Member",
                image: "/website/images/team/yori.png",
                bio: "CEO and board member with 25+ years’ experience and multiple M&As. General partner at Cognitive Ventures.",
                linkedin: "https://www.linkedin.com/in/nelken/s"
              },
              {
                name: "Arik Diament",
                role: "Co-founder & CPO",
                image: "/website/images/team/arik.png",
                bio: "20+ years building scalable, data-driven systems with deep hands-on experience in eCommerce platforms.",
                linkedin: "https://www.linkedin.com/in/arikdiamant/"
              },
              {
                name: "Kedem Diament",
                role: "Co-founder & CTO",
                image: "/website/images/team/kedem.png",
                bio: "Leads platform architecture and core production systems for scalable, production-grade infrastructure.",
                linkedin: "https://www.linkedin.com/in/kedem-diamant/"
              }
            ].map((member, i) => (
              <a 
                key={i} 
                href={member.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="group relative p-8 rounded-2xl bg-slate-900 border border-slate-800 hover:border-purple-500/30 transition-all flex flex-col items-center text-center"
              >
                {/* LinkedIn Icon - Top Right */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <Linkedin className="h-4 w-4 text-slate-500 hover:text-slate-200 transition-colors" />
                </div>

                <div className="relative h-32 w-32 mb-6 rounded-full overflow-hidden border-2 border-purple-500/20">
                  <Image 
                    src={member.image} 
                    alt={member.name} 
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                
                <h3 className="text-xl font-bold mb-1 group-hover:text-purple-400 transition-colors">
                  {member.name}
                </h3>
                
                <p className="text-purple-400 text-sm font-medium mb-4">{member.role}</p>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{member.bio}</p>
              </a>
            ))}
          </div>
          <div className="mt-16 text-center">
            <div className="inline-flex items-center space-x-2 text-slate-500 font-medium">
              <Database className="h-5 w-5" />
              <span>Based in Tel-Aviv and New York, NY</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 text-center">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-8">
            <h2 className="text-3xl font-bold">Let's talk.</h2>
            <p className="text-xl text-slate-400">
             Whether you want to collaborate, partner, invest, or simply learn more - we're here.
            </p>
            <div className="py-4">
              <WebsiteCtaLink href="/login">Start Now</WebsiteCtaLink>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
